<template>

    <v-dialog v-model="dialogModel" max-width="900">

        <toolbar-page title="Translations editor" icon="mdi-earth" help="localizations-editor" :loading="loading">
            <template slot="buttons">
                <v-tooltip left color="primary" open-delay="1000">
                    <template v-slot:activator="{ on }">
                        <v-btn @click="reloadClicked" v-on="on" icon>
                            <v-icon>mdi-reload</v-icon>
                        </v-btn>
                    </template>
                    <span>Reload</span>
                </v-tooltip>

                <v-btn color="primary" @click="saveClicked" :loading="saving">Update</v-btn>

                <v-tooltip left color="primary" open-delay="1000">
                    <template v-slot:activator="{ on }">
                        <v-btn icon elevation="0" @click="dialogModel=false" v-on="on">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </template>
                    <span>Close without selection</span>
                </v-tooltip>
            </template>
        </toolbar-page>

        <v-card>
            <v-card-text class="pt-2">

                <v-alert type="info" class="body-2 mb-0" dismissible @input="closeInfoBox">
                    These are generic translations for various dynamic elements such as buttons, labels and error
                    messages.
                    In addition to these you also need to translate the email templates and landing pages.
                </v-alert>

                <div class="mt-5">
                    <v-skeleton-loader
                        v-if="loading && skeletonLoaderEnabled"
                        v-for="(loader,index) in loaders"
                        :key="'loader'+index"
                        class="pt-3"
                        :type="loader"
                        :loading="true"
                    ></v-skeleton-loader>
                </div>

                <v-tabs v-model="activeTab"
                        dark
                        slider-color="primary"
                        color="white">

                    <v-tab v-if="localeCodes" v-for="code in localeCodes" :key="code">{{ code }}</v-tab>

                    <v-tab-item v-if="localeCodes" v-for="(code, index) in localeCodes" :key="code + '-item'"
                                class="mt-5">

                        <v-card v-if="subPanelTranslations"
                                v-for="item in subPanelTranslations[code]"
                                :key="item.id"
                                class="mt-1"
                                flat>

                            <v-card-text class="pa-0 pt-2">
                                <div class="d-flex justify-start">

                                    <div class="pa-3 pr-6 align-self-center" v-if="item.translated">
                                        <v-tooltip top color="primary" open-delay="1000">
                                            <template v-slot:activator="{ on }">
                                                <v-btn fab
                                                       x-small
                                                       left
                                                       v-on="on"
                                                       tabindex="-1"
                                                       color="green darken-2"
                                                       @click="setUntranslated(item)">
                                                    <v-icon color="white">
                                                        mdi-check-bold
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <span>This item is translated! Click to set untranslated</span>
                                        </v-tooltip>
                                    </div>

                                    <div class="pa-3 pr-6 align-self-center text-center" v-if="!item.translated">
                                        <v-tooltip top color="primary" open-delay="1000">
                                            <template v-slot:activator="{ on }">
                                                <v-btn fab
                                                       x-small
                                                       left
                                                       v-on="on"
                                                       tabindex="-1"
                                                       color="yellow darken-4"
                                                       @click="setTranslated(item)">
                                                    <v-icon color="white">
                                                        mdi-alert
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <span>This item is not translated! Click to set translated!</span>
                                        </v-tooltip>
                                    </div>

                                    <v-text-field dense
                                                  :key="item.id + '-model'"
                                                  persistent-hint
                                                  :hint="ucFirst(item.name.replaceAll('_',' '))"
                                                  @change="itemChanged(item)"
                                                  v-model="item.value"
                                                  :tabindex="item.id"
                                    ></v-text-field>

                                </div>
                            </v-card-text>
                        </v-card>

                    </v-tab-item>

                </v-tabs>

            </v-card-text>

            <json v-if="debug" :data="subPanelTranslations"></json>

        </v-card>
    </v-dialog>

</template>

<script>

import Json from 'vue-json-pretty';
import ToolbarPage from "./ToolbarPage";
import {mapActions, mapState} from 'vuex';
import ChartLoader from "./ChartLoader";

export default {

    name: "LocalizationsEditor",

    components: {
        ChartLoader,
        Json,
        ToolbarPage
    },

    props: {
        value: {
            required: true,
        },

        subPanel: {
            type: Object,
            required: true,
        }
    },

    watch: {
        value(bool) {
            if (bool) {
                this.loadTranslations();
            }
        }
    },

    data() {
        return {
            debug: false,
            activeTab: null,
            localeCodes: null,
            loading: false,
            saving: false,
            translations: null,
            showInfoBox: true,
            loaders: [
                'heading',
                'list-item-avatar-two-line',
                'list-item-avatar-two-line',
                'list-item-avatar-two-line',
                'list-item-avatar-two-line',
                'list-item-avatar-two-line',
                'actions'
            ],
            skeletonLoaderEnabled: true,
        }
    },

    computed: {

        ...mapState('SubPanels', [
            'subPanelTranslations'
        ]),

        dialogModel: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },
    },

    methods: {

        ...mapActions('SubPanels', [
            'loadSubPanelTranslations',
            'saveSubPanelTranslations'
        ]),

        setTranslated(item) {
            item.translated = true;
        },

        setUntranslated(item) {
            item.translated = false;
        },

        itemChanged(item) {
            item.translated = true;
        },

        saveClicked() {

            this.saving = true;

            this.saveSubPanelTranslations({
                id: this.subPanel.id,
                data: this.subPanelTranslations
            })
                .then(() => {
                    this.localeCodes = Object.keys(this.subPanelTranslations);

                    Bus.$emit('flash-message', {message: 'Localizations saved'})

                    this.$emit('input', false);
                })

                .catch(error => {

                    console.error(error.response);

                    Bus.$emit('flash-message', {type: 'error', message: 'OOPS We failed to save localizations...'});
                })
                .finally(() => {
                    this.saving = false;
                })
        },

        reloadClicked() {
            this.loadTranslations();
        },

        closeInfoBox() {

            localStorage.translationsEditorWarning = JSON.stringify(false);
        },

        loadTranslations() {

            this.loading = true;

            this.loadSubPanelTranslations(this.subPanel.id)

                .then(() => {
                    this.localeCodes = Object.keys(this.subPanelTranslations);
                    this.skeletonLoaderEnabled = false;
                })

                .catch(error => {

                    console.error(error.response);

                    Bus.$emit('flash-message', {type: 'error', message: 'OOPS We failed to load localizations...'});
                })
                .finally(() => {
                    this.loading = false;
                })
        }
    },

    mounted() {
        const found = localStorage.getItem('translationsEditorWarning');

        if (found) {
            this.showInfoBox = false;
        }
    }
}
</script>
